import { useCallback, useMemo } from 'react';
import { DEFAULT_COLOR_PALETTE_MAP } from 'sonata-static-storage/lib/samples/mapPreferences.samples';

import { MapViewer } from '../MapViewer/MapViewer';

import type { Layer } from 'sonata-static-storage/lib/common/common.types';
import type { LayerMapData } from 'sonata-static-storage/lib/common/itemsGrouping.types';
import type { TooltipFeature } from '../MapViewer/MapViewer';
import type { PublicCampaignMapDataPoint } from '../sharedMap.types';

const COLOR_PALETTE = DEFAULT_COLOR_PALETTE_MAP;
const INDEX_LIMITS = { max: 100, min: 0 };
const LAYER_OPACITY = 0.7;

type Props = {
  level: string;
  mapData: PublicCampaignMapDataPoint[];
  metrics: string[];
  style?: 'dark' | 'light';
};

function buildLayers(
  mapData: PublicCampaignMapDataPoint[],
  level: string
): { layerMapData: LayerMapData[]; layers: Layer[] } {
  const countries = new Set<string>();
  const layerMapData: LayerMapData[] = [];

  for (const point of mapData) {
    countries.add(point.country);
    layerMapData.push({ elementId: point.regionId, name: point.name, w: point.weight } as unknown as LayerMapData);
  }

  const layers = Array.from(countries).map(
    country =>
      ({
        _id: `sharedCampaignMap_${country}`,
        color: COLOR_PALETTE,
        config: {
          _id: `sharedCampaignMap_${country}`,
          color: COLOR_PALETTE,
          country,
          indexLimits: INDEX_LIMITS,
          opacity: LAYER_OPACITY,
          shape: level,
          type: 'index'
        },
        type: 'index'
      }) as unknown as Layer
  );

  return { layerMapData, layers };
}

type BBox = [number, number, number, number];

function unionBoundingBox(mapData: PublicCampaignMapDataPoint[]): BBox | undefined {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  let found = false;

  for (const point of mapData) {
    const box = point.boundingBox;
    if (!box || box.length < 4) {
      continue;
    }
    found = true;
    west = Math.min(west, box[0]);
    south = Math.min(south, box[1]);
    east = Math.max(east, box[2]);
    north = Math.max(north, box[3]);
  }

  return found ? [west, south, east, north] : undefined;
}

function toTitleCase(metric: string): string {
  return metric
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function CampaignMapView({ level, mapData, metrics, style }: Props): JSX.Element {
  const { layerMapData, layers } = useMemo(() => buildLayers(mapData, level), [mapData, level]);

  const fitBounds = useMemo(() => unionBoundingBox(mapData), [mapData]);

  const infoByRegion = useMemo(() => {
    const info = new Map<string, { name: string; metrics: Record<string, number> }>();
    for (const point of mapData) {
      info.set(point.regionId, { metrics: point.metrics, name: point.name });
    }
    return info;
  }, [mapData]);

  const renderTooltip = useCallback(
    (feature: TooltipFeature) => {
      const info = feature.id == null ? undefined : infoByRegion.get(String(feature.id));
      if (!info) {
        return null;
      }
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{info.name}</span>
          {metrics.map(metric => {
            const value: number | undefined = info.metrics[metric];
            return (
              <span key={metric}>
                {toTitleCase(metric)}: {value === undefined ? 'N/A' : value.toLocaleString()}
              </span>
            );
          })}
        </span>
      );
    },
    [infoByRegion, metrics]
  );

  return (
    <MapViewer
      fitBounds={fitBounds}
      layerMapData={layerMapData}
      layers={layers}
      renderTooltip={renderTooltip}
      style={style}
    />
  );
}
